import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { CSSProperties } from 'react'
import type { SxProps } from '@mui/material'

import { createColors } from './colors'
import { createColorsDark } from './colorsDark'
import { spacing } from './spacing'
import { timing } from './timing'
import { typography } from './typography'
import type {
  AllowedStylesT,
  ImmutableThemeContextModeT,
  Theme,
  ThemeContextModeT,
  ThemedFnT,
  ThemedStyle,
} from './types'

export type ThemeContextType = {
  setThemeContextOverride: (newTheme: ThemeContextModeT) => void
  theme: Theme
  themeContext: ImmutableThemeContextModeT
  themed: ThemedFnT
}

export const ThemeContext = createContext<ThemeContextType | null>(null)

export interface ThemeProviderProps {
  initialContext?: ThemeContextModeT
  mainColor?: string
  iconColor?: string
  /**
   * When false, skip writing data-theme / color-scheme on documentElement.
   * Use for nested providers that force a palette for a page section without
   * changing the global document theme (e.g. public light surfaces).
   * @default true
   */
  applyToDocument?: boolean
}

/**
 * Web-compatible hook for theme storage using localStorage
 */
function useThemeStorage(key: string): [string | undefined, (value: string | undefined) => void] {
  const [value, setValue] = useState<string | undefined>(() => {
    if (typeof window === 'undefined') return undefined
    try {
      return localStorage.getItem(key) || undefined
    } catch {
      return undefined
    }
  })

  // Update localStorage when value changes
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      if (value === undefined) {
        localStorage.removeItem(key)
      } else {
        localStorage.setItem(key, value)
      }
    } catch (error) {
      console.warn('[Theme] Failed to save theme to localStorage:', error)
    }
  }, [key, value])

  // Listen for storage events (cross-tab changes)
  useEffect(() => {
    if (typeof window === 'undefined') return
    
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key) {
        setValue(e.newValue || undefined)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [key])

  return [value, setValue]
}

/**
 * The ThemeProvider is the heart and soul of the design token system. It provides a context wrapper
 * for your entire app to consume the design tokens as well as global functionality like the app's theme.
 *
 * To get started, you want to wrap your entire app's JSX hierarchy in `ThemeProvider`
 * and then use the `useAppTheme()` hook to access the theme context.
 */
export const ThemeProvider: FC<PropsWithChildren<ThemeProviderProps>> = ({
  children,
  initialContext,
  mainColor: initialMainColor = '#1976d2',
  iconColor: initialIconColor = '#000000',
  applyToDocument = true,
}) => {
  // The operating system theme:
  const prefersDarkMode = typeof window !== 'undefined' 
    ? window.matchMedia('(prefers-color-scheme: dark)').matches 
    : false
  
  // Our saved theme context: can be "light", "dark", or undefined (system theme)
  const [themeScheme, setThemeScheme] = useThemeStorage('theme')

  // Dynamic colors (can be changed at runtime)
  const [mainColor, setMainColor] = useState(initialMainColor)
  const [iconColor, setIconColor] = useState(initialIconColor)

  /**
   * This function is used to set the theme context and is exported from the useAppTheme() hook.
   *  - setThemeContextOverride("dark") sets the app theme to dark no matter what the system theme is.
   *  - setThemeContextOverride("light") sets the app theme to light no matter what the system theme is.
   *  - setThemeContextOverride(undefined) the app will follow the operating system theme.
   */
  const setThemeContextOverride = useCallback(
    (newTheme: ThemeContextModeT) => {
      setThemeScheme(newTheme)
    },
    [setThemeScheme],
  )

  /**
   * initialContext is the theme context passed in from the app.tsx file and always takes precedence.
   * themeScheme is the value from localStorage. If undefined, we fall back to the system theme
   * prefersDarkMode is the value from the device. If undefined, we fall back to "light"
   */
  const themeContext: ImmutableThemeContextModeT = useMemo(() => {
    const t = initialContext || themeScheme || (prefersDarkMode ? 'dark' : 'light')
    return t === 'dark' ? 'dark' : 'light'
  }, [initialContext, themeScheme, prefersDarkMode])

  // Update document attribute for CSS theme variables and browser chrome
  useEffect(() => {
    if (!applyToDocument || typeof document === 'undefined') return
    const root = document.documentElement
    root.setAttribute('data-theme', themeContext)
    root.style.colorScheme = themeContext
  }, [themeContext, applyToDocument])

  // Create theme with dynamic colors
  const theme: Theme = useMemo(() => {
    const colors = themeContext === 'dark' 
      ? createColorsDark(mainColor, iconColor)
      : createColors(mainColor, iconColor)

    return {
      colors,
      spacing,
      typography,
      timing,
      borderRadius: {
        xs: '2px',
        sm: '4px',
        md: '8px',
        lg: '16px',
      },
      isDark: themeContext === 'dark',
    }
  }, [themeContext, mainColor, iconColor])

  const themed = useCallback(
    <T,>(styleOrStyleFn: AllowedStylesT<T>) => {
      const flatStyles = [styleOrStyleFn].flat(3) as (ThemedStyle<T> | T)[]
      const stylesArray = flatStyles.map((f) => {
        if (typeof f === 'function') {
          return (f as ThemedStyle<T>)(theme)
        } else {
          return f
        }
      })
      // Flatten the array of styles into a single object
      return Object.assign({}, ...stylesArray) as T
    },
    [theme],
  )

  const value = {
    theme,
    themeContext,
    setThemeContextOverride,
    themed,
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

/**
 * This is the primary hook that you will use to access the theme context in your components.
 */
export const useAppTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useAppTheme must be used within a ThemeProvider')
  }
  return context
}
